// /performance/search — ported 1:1 from miluliem's
// apps/admin/src/pages/admin/Search.tsx. Keep the summary cards +
// queries table shape aligned when changing.

import { useEffect, useState, useCallback } from "react";
import {
  Search as SearchIcon,
  AlertTriangle,
  TrendingDown,
  ChevronLeft,
  ChevronRight,
  Hash,
  Users,
  Percent,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DateRangeSelect, {
  type DateRange,
  rangeForPreset,
  rangeToQuery,
} from "@/components/admin/DateRangeSelect";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

type Filter = "all" | "zero" | "low";

interface Summary {
  range?: { from: string | null; to: string | null; label: string };
  total_searches: number;
  distinct_queries: number;
  zero_result_searches: number;
  zero_result_rate: number | null;
  search_sessions: number;
  converted_search_sessions: number;
  search_conversion_rate: number | null;
}

interface QueryRow {
  query: string;
  query_normalized: string;
  searches: number;
  sessions: number;
  avg_results: number;
  max_results: number;
  min_results: number;
  last_searched: string;
  converted_sessions: number;
  conversion_rate_pct: number | null;
}

const PAGE_SIZE = 50;

export function SearchAnalytics() {
  const [range, setRange] = useState<DateRange>(() => rangeForPreset("last_7d"));
  const [filter, setFilter] = useState<Filter>("all");
  const [summary, setSummary] = useState<Summary | null>(null);
  const [queries, setQueries] = useState<QueryRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchSummary = useCallback(async () => {
    try {
      const { from, to } = rangeToQuery(range);
      const json = await api<{ data: Summary }>(
        `/api/v1/admin/search/summary?from=${from}&to=${to}`,
      );
      setSummary(json.data ?? null);
    } catch (err) {
      console.error("[Search] summary fetch failed", err);
    }
  }, [range]);

  const fetchQueries = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = rangeToQuery(range);
      const params = new URLSearchParams({
        from,
        to,
        filter,
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      const json = await api<{
        data: QueryRow[];
        meta: { total: number };
      }>(`/api/v1/admin/search/queries?${params}`);
      setQueries(json.data ?? []);
      setTotalCount(json.meta?.total ?? 0);
    } catch (err) {
      console.error("[Search] queries fetch failed", err);
    }
    setLoading(false);
  }, [range, filter, page]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);
  useEffect(() => {
    setPage(0);
  }, [range, filter]);
  useEffect(() => {
    fetchQueries();
  }, [fetchQueries]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const fmtPct = (v: number | null) => (v !== null ? `${(v * 100).toFixed(1)}%` : "—");

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 font-display text-xl font-bold text-foreground">
            <SearchIcon size={20} className="text-violet-500" />
            Search
          </h1>
          <p className="mt-1 font-body text-xs text-muted-foreground">
            On-site search queries, gaps, and conversion impact (vinoga.lv)
          </p>
        </div>
        <DateRangeSelect value={range} onChange={setRange} />
      </div>

      {summary && (
        <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <SummaryCard
            icon={Hash}
            label="Searches"
            value={summary.total_searches.toLocaleString()}
            sub={`${summary.distinct_queries.toLocaleString()} distinct`}
          />
          <SummaryCard
            icon={AlertTriangle}
            label="Zero results"
            value={summary.zero_result_searches.toLocaleString()}
            sub={fmtPct(summary.zero_result_rate)}
            warn={(summary.zero_result_rate ?? 0) > 0.1}
          />
          <SummaryCard
            icon={Users}
            label="Search sessions"
            value={summary.search_sessions.toLocaleString()}
            sub={`${summary.converted_search_sessions} converted`}
          />
          <SummaryCard
            icon={Percent}
            label="Search → add to cart"
            value={fmtPct(summary.search_conversion_rate)}
            sub="of search sessions"
            highlight
          />
        </section>
      )}

      <section>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Queries
            {totalCount > 0 && (
              <span className="ml-2 font-body text-xs font-normal normal-case">
                ({totalCount.toLocaleString()} unique)
              </span>
            )}
          </h2>
          <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
            <TabsList className="bg-muted">
              <TabsTrigger value="all" className="font-body text-xs">
                All queries
              </TabsTrigger>
              <TabsTrigger
                value="zero"
                className="font-body text-xs text-red-600 data-[state=active]:text-red-700"
              >
                <AlertTriangle size={12} className="mr-1" /> Zero results
              </TabsTrigger>
              <TabsTrigger
                value="low"
                className="font-body text-xs text-amber-600 data-[state=active]:text-amber-700"
              >
                <TrendingDown size={12} className="mr-1" /> Low results (1–3)
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {filter === "zero" && totalCount > 0 && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 font-body text-xs text-red-900">
            <strong>{totalCount} queries returned 0 results.</strong> These are catalog gaps —
            products customers expected to find but couldn't. Worth reviewing for missing listings,
            broken slugs, or category labels that don't match search terms.
          </div>
        )}

        {loading ? (
          <div className="p-4 font-body text-sm text-muted-foreground">Loading…</div>
        ) : queries.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">
            <SearchIcon size={40} className="mx-auto mb-3 opacity-30" />
            <p className="font-body text-sm">
              No queries match this filter for the selected window.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-border bg-card">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="font-bold">Query</TableHead>
                    <TableHead className="text-right font-bold">Searches</TableHead>
                    <TableHead className="text-right font-bold">Sessions</TableHead>
                    <TableHead className="text-right font-bold">Avg results</TableHead>
                    <TableHead className="text-right font-bold">Conversion</TableHead>
                    <TableHead className="font-bold">Last seen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {queries.map((q) => {
                    const lastSeen = new Date(q.last_searched);
                    const diffMin = (Date.now() - lastSeen.getTime()) / 60_000;
                    const lastLabel =
                      diffMin < 60
                        ? `${Math.round(diffMin)}m ago`
                        : diffMin < 60 * 24
                          ? `${Math.round(diffMin / 60)}h ago`
                          : lastSeen.toLocaleDateString("lv", {
                              day: "2-digit",
                              month: "short",
                            });
                    const isZero = q.max_results === 0;
                    const isLow = !isZero && q.max_results <= 3;
                    return (
                      <TableRow key={q.query_normalized}>
                        <TableCell className="py-3 font-body text-sm text-foreground">
                          <a
                            href={`https://vinoga.lv/meklesana?q=${encodeURIComponent(q.query)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="transition-colors hover:text-primary"
                          >
                            {q.query}
                          </a>
                        </TableCell>
                        <TableCell className="py-3 text-right font-body text-sm text-foreground">
                          {q.searches}
                        </TableCell>
                        <TableCell className="py-3 text-right font-body text-xs text-muted-foreground">
                          {q.sessions || "—"}
                        </TableCell>
                        <TableCell
                          className={`py-3 text-right font-body text-xs ${
                            isZero
                              ? "font-semibold text-red-600"
                              : isLow
                                ? "font-semibold text-amber-600"
                                : "text-muted-foreground"
                          }`}
                        >
                          {q.avg_results}
                        </TableCell>
                        <TableCell className="py-3 text-right font-body text-xs text-foreground">
                          {q.converted_sessions > 0 ? (
                            <span>
                              {q.converted_sessions} ({q.conversion_rate_pct}%)
                            </span>
                          ) : (
                            <span className="text-muted-foreground/40">—</span>
                          )}
                        </TableCell>
                        <TableCell className="py-3 font-body text-xs text-muted-foreground">
                          {lastLabel}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {totalPages > 1 && (
              <div className="mt-3 flex items-center justify-between px-1">
                <span className="font-body text-xs text-muted-foreground">
                  Showing {page * PAGE_SIZE + 1}–
                  {Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount.toLocaleString()}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2"
                    disabled={page === 0}
                    onClick={() => setPage(0)}
                  >
                    «
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2"
                    disabled={page === 0}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeft size={14} />
                  </Button>
                  <span className="px-3 font-body text-xs text-foreground">
                    Page {page + 1} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRight size={14} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage(totalPages - 1)}
                  >
                    »
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  sub,
  highlight,
  warn,
}: {
  icon: typeof SearchIcon;
  label: string;
  value: string;
  sub?: string;
  highlight?: boolean;
  warn?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border bg-card p-3 ${
        warn
          ? "border-red-200 bg-red-50/50"
          : highlight
            ? "border-emerald-200 bg-emerald-50/30"
            : "border-border"
      }`}
    >
      <div className="mb-1 flex items-center gap-1.5 text-muted-foreground">
        <Icon size={12} />
        <span className="font-body text-xs uppercase tracking-wider">{label}</span>
      </div>
      <div
        className={`font-display text-xl font-bold ${warn ? "text-red-700" : "text-foreground"}`}
      >
        {value}
      </div>
      {sub && <div className="mt-0.5 font-body text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}
